import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Bookmark } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { FeedPage } from '@up2d8/contracts';
import { Card, EmptyState, LoadingState, PostHead, relativeTime } from '@up2d8/ui';
import { useSession } from '../../shared/session';
import { renderAppTabLink, useAppTabs } from './app-tabs';
import { DeskShell } from './desk/desk-shell';
import { MessagesIcon } from './messages-icon';
import { NotificationBell } from './notification-bell';

/**
 * De berichten die je opzij zette (SCH-1).
 *
 * **Een plank, geen tweede tijdlijn.** Daarom staan hier geen likes, reacties of
 * bevestigingsbalken: je komt hier om iets terug te vinden, niet om bij te lezen. Eén tik brengt
 * je naar het bericht zelf, en daar staat alles.
 *
 * Onder water is het wél dezelfde tijdlijn: `GET /feed?saved=true`, met dezelfde doelgroep en
 * dezelfde momentopname van ontvangers (ADR-0036). Eén plek waar "mag ik dit zien" uitgerekend
 * wordt.
 *
 * **Geen offline kopie.** De feed bewaart zijn eerste pagina voor een telefoon zonder bereik; deze
 * lijst doet dat met opzet niet. Zou hij het wel doen, dan overschreef hij de kopie van de echte
 * tijdlijn — en dan staat er na een herstart een plank waar de tijdlijn hoorde te staan.
 */
export function AppSavedPage() {
  const { t } = useTranslation();
  const { api } = useSession();
  const tabs = useAppTabs();

  const saved = useQuery({
    queryKey: ['feed', 'saved'],
    queryFn: async (): Promise<FeedPage> => {
      const { data, error } = await api.GET('/api/v1/feed', {
        params: { query: { saved: true, limit: 50 } },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as FeedPage;
    },
  });

  const items = saved.data?.items ?? [];

  return (
    <DeskShell
      messages={<MessagesIcon />}
      bell={<NotificationBell />}
      tabs={tabs}
      activeKey="profile"
      renderLink={renderAppTabLink}
    >
      <h1 className="font-heading text-page-title font-semibold text-text-primary">
        {t('interaction.savedPosts')}
      </h1>

      {saved.isPending ? (
        <LoadingState rows={3} label={t('common.loading')} />
      ) : items.length === 0 ? (
        <Card>
          <EmptyState
            title={t('interaction.noSavedPosts')}
            description={t('interaction.noSavedPostsHint')}
          />
        </Card>
      ) : (
        <ul className="flex flex-col gap-element" data-testid="saved-list">
          {items.map((item) => (
            <li key={item.postId}>
              <Card className="p-0">
                <PostHead
                  author={item.author}
                  time={relativeTime(item.publishedAt, new Date(), {
                    justNow: t('feed.justNow'),
                    minutesAgo: t('feed.minutesAgo'),
                    hoursAgo: t('feed.hoursAgo'),
                    yesterday: t('feed.yesterday'),
                  })}
                  audience={item.audienceDescription}
                  right={<Bookmark aria-hidden className="h-4 w-4 shrink-0 text-brand-primary" />}
                />
                <Link
                  to="/app/posts/$postId"
                  params={{ postId: item.postId }}
                  data-testid="saved-post"
                  className="block px-5 pb-4 focus-visible:outline-none"
                >
                  <h2 className="font-heading text-card-title font-semibold text-text-primary">
                    {item.title}
                  </h2>
                  <p className="mt-1 line-clamp-2 text-copy text-text-secondary">{item.excerpt}</p>
                </Link>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </DeskShell>
  );
}
